/**
 * F10 — Admin venue edit review page.
 *
 * Route: /venues/edits/:id
 * Permission: venues:moderate
 *
 * Shows the current venue summary, a diff table of proposed changes, and
 * action buttons: Apply / Reject.
 */
import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  FormField,
  Modal,
  Spinner,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { adminVenueApi, type VenueEditForAdmin } from '../api/venue.js';
import { translateApiError } from '../auth/errors.js';
import type { VenueEditDiff } from '@bravemove/types';

// V-17 — the backend requires a ≥10-char rejection reason
// (admin-venue-edits.ts RejectBodySchema); keep the client gate aligned so
// short reasons fail in the form instead of as a generic 422 toast.
const ReasonSchema = z.object({ reason: z.string().min(10, 'At least 10 chars').max(500) });
type ReasonValues = z.infer<typeof ReasonSchema>;

// ─── Date helper ────────────────────────────────────────────────────────────────

function formatDateTime(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

// ─── Diff display ─────────────────────────────────────────────────────────────

function DiffTable({ fieldChanges }: { fieldChanges: VenueEditDiff }) {
  const { t } = useI18n();
  const entries = Object.entries(fieldChanges) as [string, { old: unknown; new: unknown }][];

  if (entries.length === 0) {
    return <p className="text-[12.5px] text-ink-3">{t('admin:venue.edit.noChanges')}</p>;
  }

  const renderValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  return (
    <div className="overflow-x-auto rounded-lg border border-outline">
      <table className="w-full text-[12.5px]">
        <thead>
          <tr className="border-b border-outline bg-surface-2">
            <th className="text-start px-3 py-2 text-ink-3 font-semibold w-1/4">
              {t('admin:venue.edit.field')}
            </th>
            <th className="text-start px-3 py-2 text-ink-3 font-semibold w-[37.5%]">
              {t('admin:venue.edit.oldValue')}
            </th>
            <th className="text-start px-3 py-2 text-ink-3 font-semibold w-[37.5%]">
              {t('admin:venue.edit.newValue')}
            </th>
          </tr>
        </thead>
        <tbody>
          {entries.map(([field, { old, new: newVal }]) => (
            <tr key={field} className="border-b border-outline last:border-0">
              <td className="px-3 py-2 font-mono text-[11.5px] text-ink-2 align-top">{field}</td>
              <td className="px-3 py-2 text-ink-3 align-top whitespace-pre-wrap">
                {renderValue(old)}
              </td>
              <td className="px-3 py-2 text-success align-top whitespace-pre-wrap">
                {renderValue(newVal)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Reject modal ─────────────────────────────────────────────────────────────

function RejectModal({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: string) => void;
}) {
  const { t } = useI18n();
  const form = useForm<ReasonValues>({
    resolver: zodResolver(ReasonSchema),
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) form.reset({ reason: '' });
  }, [open, form]);

  const submit = form.handleSubmit((values) => {
    onSuccess(values.reason);
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('admin:venue.modals.rejectEditTitle')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('admin:common.cancel')}
          </Button>
          <Button
            variant="destructive"
            loading={form.formState.isSubmitting}
            onClick={() => void submit()}
          >
            {t('admin:venue.actions.reject')}
          </Button>
        </>
      }
    >
      <FormField
        label={t('admin:venue.modals.rejectEditReasonLabel')}
        error={form.formState.errors.reason?.message}
        required
      >
        <Textarea
          rows={4}
          maxLength={500}
          showCounter
          placeholder={t('admin:venue.modals.rejectEditReasonPlaceholder')}
          {...form.register('reason')}
        />
      </FormField>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function AdminVenueEditReviewPage() {
  const { t, locale } = useI18n();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [edit, setEdit] = useState<VenueEditForAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyError, setApplyError] = useState<string | null>(null);

  const [rejectOpen, setRejectOpen] = useState(false);

  const fetchDetail = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminVenueApi.getEdit(id);
      setEdit(data);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleApply = async () => {
    if (!edit) return;
    setApplyLoading(true);
    setApplyError(null);
    try {
      await adminVenueApi.applyEdit(edit.id);
      setApplyOpen(false);
      toast.push({ tone: 'success', title: t('admin:venue.toast.applySuccess') });
      navigate(`/venues/${edit.venueId}`);
    } catch (err) {
      setApplyError(translateApiError(err, t));
    } finally {
      setApplyLoading(false);
    }
  };

  const handleReject = async (reason: string) => {
    if (!edit) return;
    try {
      await adminVenueApi.rejectEdit(edit.id, reason);
      setRejectOpen(false);
      toast.push({ tone: 'success', title: t('admin:venue.toast.rejectEditSuccess') });
      navigate('/venues/edits');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner size={32} />
      </div>
    );
  }

  if (error || !edit) {
    return (
      <div className="space-y-4">
        <Banner tone="danger">{error ?? t('admin:errors.generic')}</Banner>
        <Button variant="ghost" onClick={() => navigate(-1)}>
          {t('admin:common.back')}
        </Button>
      </div>
    );
  }

  const isPending = edit.status === 'pending';

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-h1 font-bold text-ink">{t('admin:venue.pageTitle.editDetail')}</h1>
      </div>

      {/* Current venue summary */}
      <Card>
        <h3 className="text-[13px] font-semibold text-ink mb-2">
          {t('admin:venue.edit.currentVenue')}
        </h3>
        <div className="flex items-center gap-2">
          <Link
            to={`/venues/${edit.venueId}`}
            className="font-medium text-[14px] text-accent hover:underline"
          >
            {edit.venue.name}
          </Link>
          <span className="font-mono text-[11px] text-ink-3">/v/{edit.venue.slug}</span>
          <Badge
            tone={isPending ? 'warning' : edit.status === 'applied' ? 'success' : 'danger'}
            size="sm"
          >
            {t(`admin:venue.editStatus.${edit.status}`)}
          </Badge>
        </div>
      </Card>

      {/* Submitted by */}
      <Card>
        <h3 className="text-[13px] font-semibold text-ink mb-3">
          {t('admin:venue.edit.submittedBy', { defaultValue: 'Submitted by' })}
        </h3>
        <dl className="space-y-2.5 text-[12.5px]">
          {/* Name + registered/anonymous badge */}
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-3 w-32 shrink-0">
              {t('admin:venue.edit.submitterName', { defaultValue: 'Name' })}
            </dt>
            <dd className="flex flex-wrap items-center gap-2 text-ink">
              {edit.submitter?.displayName ? (
                edit.submitter.isRegistered && edit.submitter.userId ? (
                  <Link
                    to={`/users/${edit.submitter.userId}`}
                    className="font-medium text-accent hover:underline"
                  >
                    {edit.submitter.displayName}
                  </Link>
                ) : (
                  <span className="font-medium">{edit.submitter.displayName}</span>
                )
              ) : (
                <span className="text-ink-2">
                  {t('admin:venue.edit.submitterAnonymous', {
                    defaultValue: 'Anonymous / guest',
                  })}
                </span>
              )}
              {edit.submitter?.isRegistered ? (
                <Badge tone="info" size="sm">
                  {t('admin:venue.edit.submitterRegistered', { defaultValue: 'Registered' })}
                </Badge>
              ) : (
                <Badge tone="neutral" size="sm">
                  {t('admin:venue.edit.submitterAnonymousBadge', { defaultValue: 'Anonymous' })}
                </Badge>
              )}
            </dd>
          </div>

          {/* Email */}
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-3 w-32 shrink-0">
              {t('admin:venue.edit.submitterEmail', { defaultValue: 'Email' })}
            </dt>
            <dd className="text-ink break-all">{edit.submitter?.email ?? '—'}</dd>
          </div>

          {/* Submitted on */}
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-3 w-32 shrink-0">
              {t('admin:venue.edit.submittedOn', { defaultValue: 'Submitted on' })}
            </dt>
            <dd className="text-ink">{formatDateTime(edit.createdAt, locale)}</dd>
          </div>

          {/* Country */}
          <div className="flex items-baseline gap-2">
            <dt className="text-ink-3 w-32 shrink-0">
              {t('admin:venue.edit.submitterCountry', { defaultValue: 'Country' })}
            </dt>
            <dd className="text-ink">{edit.countryCode}</dd>
          </div>

          {/* IP fingerprint (forensics) */}
          {edit.submitterIpHash ? (
            <div className="flex items-baseline gap-2">
              <dt className="text-ink-3 w-32 shrink-0">
                {t('admin:venue.edit.submitterIpFingerprint', { defaultValue: 'IP fingerprint' })}
              </dt>
              <dd className="font-mono text-[11px] text-ink-2">
                {edit.submitterIpHash.slice(0, 12)}…
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      {/* Diff display */}
      <div className="space-y-3">
        <h3 className="text-[13px] font-semibold text-ink">
          {t('admin:venue.edit.diffTable')} <span>({edit.fieldCount})</span>
        </h3>
        <DiffTable fieldChanges={edit.fieldChanges} />
      </div>

      {/* Action bar */}
      {isPending ? (
        <div className="flex flex-wrap gap-3 pt-2">
          <Button
            variant="primary"
            onClick={() => {
              setApplyError(null);
              setApplyOpen(true);
            }}
          >
            {t('admin:venue.actions.apply')}
          </Button>
          <Button variant="destructive" onClick={() => setRejectOpen(true)}>
            {t('admin:venue.actions.reject')}
          </Button>
        </div>
      ) : null}

      {/* Apply dialog */}
      <Dialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onConfirm={() => void handleApply()}
        title={t('admin:venue.modals.applyEditTitle')}
        loading={applyLoading}
        confirmLabel={t('admin:venue.actions.apply')}
        cancelLabel={t('admin:common.cancel')}
      >
        <p className="text-[13px] text-ink-2">{t('admin:venue.modals.applyEditBody')}</p>
        {applyError ? (
          <Banner tone="danger" className="mt-3">
            {applyError}
          </Banner>
        ) : null}
      </Dialog>

      {/* Reject modal */}
      <RejectModal
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onSuccess={(reason) => void handleReject(reason)}
      />
    </div>
  );
}
